/**
 * Transport layer — postMessage-обёртка для коммуникации SDK (внутри iframe)
 * с родительским окном (igraytut.ru).
 *
 * Протокол:
 *   SDK → parent: { type: "igt:req", id: number, method: string, params: any }
 *   parent → SDK: { type: "igt:res", id: number, ok: boolean, data?: any, error?: string }
 *
 * Асинхронные события (ad opened, покупка завершена) — parent шлёт
 * { type: "igt:evt", event: string, data: any }
 *
 * Safety:
 *   - SDK проверяет `origin` входящих сообщений (должен быть родительским)
 *   - Каждому запросу присваивается уникальный id для matching'а ответа
 *   - Timeout 30 сек — если parent не ответил, reject'им с ошибкой
 */

import { SDK_ERROR_CODES, SDKError, type SDKErrorCode } from "./errors";

const REQ_TIMEOUT_MS = 30_000;

/**
 * Whitelist родительских origin'ов, которым SDK разрешает слать postMessage.
 *
 * `targetOrigin: "*"` ранее был уязвимостью: если игру встроили в сторонний
 * сайт, он получал бы postMessage с сессионными данными. Аудит 16 H-1.
 *
 * В dev localhost разрешён любого порта. На production только два известных
 * хоста igraytut.ru и play.igraytut.ru.
 */
const ALLOWED_PARENT_ORIGINS = [
  "https://igraytut.ru",
  "https://www.igraytut.ru",
  "https://play.igraytut.ru",
];

function isAllowedParentOrigin(origin: string): boolean {
  if (!origin) return false;
  if (ALLOWED_PARENT_ORIGINS.includes(origin)) return true;
  // В dev localhost любого порта
  if (/^https?:\/\/localhost(:\d+)?$/.test(origin)) return true;
  if (/^https?:\/\/127\.0\.0\.1(:\d+)?$/.test(origin)) return true;
  return false;
}

type PendingRequest = {
  resolve: (data: unknown) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
};

type EventHandler = (data: unknown) => void;

export class Transport {
  private nextId = 1;
  private pending = new Map<number, PendingRequest>();
  private listeners = new Map<string, Set<EventHandler>>();
  private parentOrigin: string | null = null;
  private debug: boolean;

  constructor(debug = false) {
    this.debug = debug;
    window.addEventListener("message", this.handleMessage);
  }

  private handleMessage = (event: MessageEvent) => {
    // Защита: игнорируем сообщения не от родителя
    if (event.source !== window.parent) return;

    // Отвергаем сообщения от неизвестных origin: защита от игры внутри
    // iframe на вредоносном сайте. Аудит 16 H-1.
    if (!isAllowedParentOrigin(event.origin)) {
      if (this.debug) console.warn("[IgrayTut SDK] rejected origin:", event.origin);
      return;
    }

    const data = event.data as { type?: string; [k: string]: unknown } | null;
    if (!data || typeof data !== "object" || typeof data.type !== "string") return;
    if (!data.type.startsWith("igt:")) return;

    // Запоминаем origin родителя, используем его как targetOrigin в ответных
    // postMessage вместо "*".
    if (!this.parentOrigin) {
      this.parentOrigin = event.origin;
    }

    if (this.debug) console.log("[IgrayTut SDK] ←", data);

    if (data.type === "igt:res") {
      const id = data.id as number;
      const p = this.pending.get(id);
      if (!p) return;
      clearTimeout(p.timer);
      this.pending.delete(id);
      if (data.ok) {
        p.resolve(data.data);
      } else {
        // Поддерживаем два формата error:
        //   - старый: error: "сообщение строкой"
        //   - новый: error: { code, message, status?, retryAfterMs?, details? }
        // Всегда бросаем SDKError чтобы игра могла делать `e instanceof SDKError`.
        const raw = data.error as unknown;
        if (raw && typeof raw === "object" && !Array.isArray(raw)) {
          const e = raw as {
            code?: string;
            message?: string;
            status?: number;
            retryAfterMs?: number;
            details?: unknown;
          };
          p.reject(
            new SDKError(e.message ?? "unknown_error", {
              code: (e.code as SDKErrorCode) ?? SDK_ERROR_CODES.UNKNOWN,
              status: e.status,
              retryAfterMs: e.retryAfterMs,
              details: e.details,
            }),
          );
        } else {
          p.reject(
            new SDKError(typeof raw === "string" ? raw : "unknown_error", {
              code: SDK_ERROR_CODES.UNKNOWN,
            }),
          );
        }
      }
    } else if (data.type === "igt:evt") {
      const event = data.event as string;
      const handlers = this.listeners.get(event);
      handlers?.forEach((h) => {
        try {
          h(data.data);
        } catch (err) {
          console.error("[IgrayTut SDK] event handler error:", err);
        }
      });
    }
  };

  /** Отправить запрос родителю и дождаться ответа. */
  request<T = unknown>(method: string, params: unknown = {}): Promise<T> {
    const id = this.nextId++;
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(
          new SDKError(`timeout: ${method}`, { code: SDK_ERROR_CODES.TIMEOUT }),
        );
      }, REQ_TIMEOUT_MS);

      this.pending.set(id, {
        resolve: resolve as (v: unknown) => void,
        reject,
        timer,
      });

      const message = { type: "igt:req", id, method, params };
      if (this.debug) console.log("[IgrayTut SDK] →", message);
      this.sendToParent(message);
    });
  }

  /** Послать родителю одностороннее сообщение (для «я готов»-событий). */
  notify(event: string, data: unknown = {}): void {
    const message = { type: "igt:notify", event, data };
    if (this.debug) console.log("[IgrayTut SDK] →", message);
    this.sendToParent(message);
  }

  /**
   * Единая точка отправки postMessage. Если `parentOrigin` уже известен
   * (после первого полученного сообщения), используем его как targetOrigin,
   * иначе пробуем whitelist по очереди до первого успеха.
   *
   * targetOrigin "*" не используется никогда: лучше отправить несколько раз
   * на каждый whitelisted origin, чем открыть потенциальный listen window
   * сторонних сайтов. Аудит 16 H-1.
   */
  private sendToParent(message: unknown): void {
    if (this.parentOrigin) {
      window.parent.postMessage(message, this.parentOrigin);
      return;
    }
    // До получения первого сообщения от parent мы не знаем origin. Шлём на все
    // whitelisted (браузер отбросит те, у которых неподходящий origin).
    for (const origin of ALLOWED_PARENT_ORIGINS) {
      try {
        window.parent.postMessage(message, origin);
      } catch {
        // invalid origin, игнорируем
      }
    }
  }

  /** Подписка на события от родителя. */
  on(event: string, handler: EventHandler): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(handler);
    return () => set.delete(handler);
  }

  destroy(): void {
    window.removeEventListener("message", this.handleMessage);
    for (const p of this.pending.values()) {
      clearTimeout(p.timer);
      p.reject(new SDKError("transport_destroyed", { code: SDK_ERROR_CODES.NETWORK_ERROR }));
    }
    this.pending.clear();
    this.listeners.clear();
  }
}
